import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Menu, X, ChevronRight, User, LogOut, LayoutDashboard, Home, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PRIMARY_WEBSITE = 'https://www.3dprintbureau.au';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${API}/settings/global`);
        if (response.data?.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
      } catch (error) {
        console.error('Failed to fetch logo:', error);
      }
    };
    fetchLogo();
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#050505]/95 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt="3D Print Bureau" className="h-16 md:h-20 lg:h-24 w-auto object-contain" />
            ) : (
              <>
                <span className="text-[#CCFF00] font-bold text-2xl font-['Chivo']">3D</span>
                <span className="text-white font-semibold text-lg hidden sm:block">PRINT BUREAU</span>
              </>
            )}
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {/* Home - External Link */}
            <a
              href={PRIMARY_WEBSITE}
              className="flex items-center gap-1 text-white/70 hover:text-[#CCFF00] transition-colors text-sm font-medium uppercase tracking-wide"
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              Home
            </a>

            {/* Get Quote - Internal */}
            <Link
              to="/"
              className={`text-sm font-medium uppercase tracking-wide transition-colors ${
                isActive('/') ? 'text-[#CCFF00]' : 'text-white/70 hover:text-[#CCFF00]'
              }`}
              data-testid="nav-quote"
            >
              Instant Quote
            </Link>

            {/* Track Order - Internal */}
            <Link
              to="/track-order"
              className={`text-sm font-medium uppercase tracking-wide transition-colors ${
                isActive('/track-order') ? 'text-[#CCFF00]' : 'text-white/70 hover:text-[#CCFF00]'
              }`}
              data-testid="nav-track"
            >
              Track Order
            </Link>
          </div>

          {/* CTA & Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button 
                    variant={isActive('/dashboard') || location.pathname.startsWith('/admin') ? 'default' : 'outline'}
                    className={isActive('/dashboard') || location.pathname.startsWith('/admin') 
                      ? 'bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none px-6'
                      : 'border-white/20 bg-transparent text-white hover:bg-white/5 rounded-none'}
                    data-testid="dashboard-btn"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {isAdmin ? 'Operations' : 'Dashboard'}
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="border-white/20 bg-transparent text-white hover:bg-white/5 rounded-none"
                      data-testid="user-menu-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {user.name?.split(' ')[0] || 'Account'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#111] border-white/10 text-white">
                    {!isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="flex items-center cursor-pointer">
                          <User className="w-4 h-4 mr-2" />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      onClick={logout}
                      className="text-red-400 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/login">
                <Button 
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none px-6"
                  data-testid="login-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Manage Orders
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <a
                href={PRIMARY_WEBSITE}
                className="flex items-center gap-2 text-white/70 hover:text-[#CCFF00] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                <Home className="w-4 h-4" />
                Home
                <ExternalLink className="w-3 h-3 ml-auto" />
              </a>
              <Link
                to="/"
                className="text-white/70 hover:text-[#CCFF00] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Instant Quote
              </Link>
              <Link
                to="/track-order"
                className="text-white/70 hover:text-[#CCFF00] transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Track Order
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase rounded-none">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {isAdmin ? 'Operations' : 'Dashboard'}
                    </Button>
                  </Link>
                  {!isAdmin && (
                    <Link to="/profile" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full border-white/20 text-white rounded-none">
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="ghost" 
                    className="w-full text-red-400 justify-start"
                    onClick={() => { logout(); setIsOpen(false); }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase rounded-none">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Manage Orders
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
